/**
 * Cart Utilities
 * 
 * Helper functions for managing Shopify cart
 */

export class Cart {
  constructor() {
    this.endpoint = '/cart.js';
  }

  /**
   * Get current cart state
   */
  async get() {
    try {
      const response = await fetch(this.endpoint);
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      throw error;
    }
  }

  /**
   * Add item to cart
   */
  async add(items) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: Array.isArray(items) ? items : [items] }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }
      
      const data = await response.json();
      this.dispatchEvent('cart:add', data);
      return data;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  /**
   * Update cart item quantity
   */
  async update(updates) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates }),
      });
      
      const data = await response.json();
      this.dispatchEvent('cart:update', data);
      return data;
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    }
  }

  /**
   * Change cart item line
   */
  async change(line, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ line, quantity }),
      });
      
      const data = await response.json();
      this.dispatchEvent('cart:change', data);
      return data;
    } catch (error) {
      console.error('Error changing cart:', error);
      throw error;
    }
  }

  /**
   * Clear cart
   */
  async clear() {
    try {
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
      });
      
      const data = await response.json();
      this.dispatchEvent('cart:clear', data);
      return data;
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw error;
    }
  }

  /**
   * Dispatch custom cart events
   */
  dispatchEvent(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  }

  /**
   * Update cart count in UI
   */
  async updateCount() {
    const cart = await this.get();
    const countElements = document.querySelectorAll('[data-cart-count]');
    
    countElements.forEach((element) => {
      element.textContent = cart.item_count;
      element.style.display = cart.item_count > 0 ? 'block' : 'none';
    });
  }
}

// Create singleton instance
const cart = new Cart();

// Initialize cart count on page load
export function initCart() {
  cart.updateCount();
  
  // Listen for cart events to update count
  ['cart:add', 'cart:update', 'cart:change', 'cart:clear'].forEach((event) => {
    document.addEventListener(event, () => cart.updateCount());
  });
}

/**
 * Helper function to add items to cart
 * @param {string|number} variantId - The variant ID to add
 * @param {number} quantity - Quantity to add (default: 1)
 * @param {object} properties - Optional line item properties
 * @returns {Promise} Cart data
 */
export async function addToCart(variantId, quantity = 1, properties = {}) {
  return cart.add({
    id: variantId,
    quantity,
    properties,
  });
}

/**
 * Helper function to get current cart
 * @returns {Promise} Cart data
 */
export async function getCart() {
  return cart.get();
}

/**
 * Helper function to update cart
 * @param {object} updates - Updates object with variant IDs as keys
 * @returns {Promise} Cart data
 */
export async function updateCart(updates) {
  return cart.update(updates);
}

export default cart;

